"use client";

import { useState } from "react";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { FreeSubscription } from "@/common/model/FreeSubscription";
import { FreeSubscriptionList } from "./FreeSubscriptionList";
import { FreeSubscriptionModal } from "./FreeSubscriptionModal";
import { FreeSubscriptionService } from "@/services/FreeSubscriptionService";
import { User } from "@/common/model/User";
import { UserSearchInput } from "../ui";
import ConfirmDeleteModal from "../shared/ConfirmDeleteModal";
import FreeSubscriptionTabs from "./FreeSubscriptionTabs";

type FreeSubscriptionManagerProps = {
  subscriptions: FreeSubscription[];
  selectedTab: string;
  selectedUser: User | null;
  onSelectTab: (tab: string) => void;
  onSelectUser: (user: User | null) => void;
  setSubscriptions: (subscriptions: FreeSubscription[]) => void;
};

const FreeSubscriptionManager: React.FC<FreeSubscriptionManagerProps> = ({
  subscriptions,
  selectedTab,
  selectedUser,
  onSelectTab,
  onSelectUser,
  setSubscriptions,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentSubscription, setCurrentSubscription] =
    useState<FreeSubscription | null>(null);

  const handleAddSubscription = () => {
    setCurrentSubscription(null);
    setIsModalOpen(true);
  };

  const handleEditSubscription = (subscription: FreeSubscription) => {
    setCurrentSubscription(subscription);
    setIsModalOpen(true);
  };

  const handleEndNowSubscription = async (subscription: FreeSubscription) => {
    await FreeSubscriptionService.endNow(subscription);
    fetchSubscriptions();
  };

  const handleDeleteSubscription = (subscription: FreeSubscription) => {
    setCurrentSubscription(subscription);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (currentSubscription) {
      await FreeSubscriptionService.delete(currentSubscription.id.toString());
      fetchSubscriptions();
      setCurrentSubscription(null);
      setIsDeleteModalOpen(false);
    }
  };

  const fetchSubscriptions = async () => {
    setError(null);
    const updatedSubscriptions = await FreeSubscriptionService.fetchAll();
    setSubscriptions(updatedSubscriptions);
  };

  const saveSubscription = async (subscription: Partial<FreeSubscription>) => {
    setError(null);
    try {
      if (currentSubscription) {
        await FreeSubscriptionService.update(
          currentSubscription.id.toString(),
          subscription
        );
      } else {
        await FreeSubscriptionService.create(subscription);
      }
      fetchSubscriptions();
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : "Unknown error");
    }
    setIsModalOpen(false);
  };

  return (
    <div className="min-h-screen p-2 lg:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="w-full flex flex-col lg:flex-row gap-4 justify-between items-center mb-8">
          <h1 className="w-full text-xl lg:text-3xl font-bold text-balance">
            Free Subscriptions list
          </h1>
          <Button onClick={handleAddSubscription}>
            <PlusIcon className="mr-2 size-5" /> Add subscription
          </Button>
        </div>
        {error && (
          <p className="text-red-500 mb-4 text-center text-xl">{error}</p>
        )}
        <FreeSubscriptionTabs
          onSelect={(tab) => onSelectTab(tab)}
          selectedTab={selectedTab}
        />
        <UserSearchInput
          theme="light"
          onUserSelect={(selectedUser: User) => {
            onSelectUser(selectedUser);
          }}
        />
        {selectedUser && (
          <div className="flex items-center justify-between mb-2">
            <p>Showing subscriptions of {selectedUser.name}</p>
            <XMarkIcon
              className="size-6 cursor-pointer hover:text-lightshadowblue2"
              onClick={() => onSelectUser(null)}
            />
          </div>
        )}
        <FreeSubscriptionList
          subscriptions={subscriptions}
          onEdit={handleEditSubscription}
          onEndNow={handleEndNowSubscription}
          onDelete={handleDeleteSubscription}
        />
        <FreeSubscriptionModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={saveSubscription}
          subscription={currentSubscription}
        />
        <ConfirmDeleteModal
          isOpen={isDeleteModalOpen}
          itemName={currentSubscription?.userName || ""}
          itemLabel="subscription"
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={confirmDelete}
        />
      </div>
    </div>
  );
};

export default FreeSubscriptionManager;
